import calculatePayment, { monthlyPMI } from "@zillow/mortgage-calculations";
import { useLastValidValues } from "../../utils/form/useLastValidValues.esm.js";
import { LOAN_PROGRAMS, COMMON_PERCENT_CONFIG, COMMON_AMOUNT_CONFIG, LOAN_TERM_IN_MONTHS } from "../../utils/zhl-calculator.esm.js";
const ARM_PROGRAMS = [LOAN_PROGRAMS.arm3, LOAN_PROGRAMS.arm5, LOAN_PROGRAMS.arm7];
const FieldConfig = {
  homePrice: {
    name: "homePrice",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  downPayment: {
    name: "downPayment",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  downPaymentPercent: {
    name: "downPaymentPercent",
    config: {
      ...COMMON_PERCENT_CONFIG
    }
  },
  zipCode: {
    name: "zipCode"
  },
  loanProgram: {
    name: "loanProgram",
    options: [{
      value: LOAN_PROGRAMS.fixed30Year,
      label: "30-year fixed"
    }, {
      value: LOAN_PROGRAMS.fixed15Year,
      label: "15-year fixed"
    }, {
      value: LOAN_PROGRAMS.arm5,
      label: "5-year ARM"
    }],
    config: {
      required: "Required",
      validate: {
        invalidProgram: (value) => {
          let error = null;
          if (Object.values(LOAN_PROGRAMS).includes(value) === false) {
            error = "Invalid loan program";
          }
          return error;
        }
      }
    }
  },
  fhaLoanProgram: {
    name: "loanProgram",
    options: [{
      value: LOAN_PROGRAMS.fixed30Year,
      label: "30-year fixed"
    }, {
      value: LOAN_PROGRAMS.fixed15Year,
      label: "15-year fixed"
    }]
  },
  interestRate: {
    name: "interestRate",
    config: {
      ...COMMON_PERCENT_CONFIG
    }
  },
  includeTaxesInsurance: {
    name: "includeTaxesInsurance"
  },
  includeMIP: {
    name: "includeMIP"
  },
  taxRate: {
    name: "taxRate",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  taxRatePercent: {
    name: "taxRatePercent",
    config: {
      ...COMMON_PERCENT_CONFIG
    }
  },
  homeInsurance: {
    name: "homeInsurance",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  hoa: {
    name: "hoa",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  includePMI: {
    name: "includePMI"
  }
};
function useLastValidPayemntValues(form) {
  return useLastValidValues(form, getPayementValues);
}
function getPayementValues(lastValidFields) {
  const calculations = calculatePayment({
    term: LOAN_TERM_IN_MONTHS[lastValidFields.loanProgram],
    homePrice: lastValidFields.homePrice,
    downPayment: lastValidFields.downPayment,
    rate: lastValidFields.interestRate,
    includeHOA: true,
    monthlyHOA: lastValidFields.hoa,
    includeHomeownersInsurance: lastValidFields.includeTaxesInsurance,
    annualHomeownersInsurance: lastValidFields.homeInsurance,
    includePropertyTax: lastValidFields.includeTaxesInsurance,
    propertyTaxAmount: lastValidFields.taxRate,
    propertyTaxRate: lastValidFields.taxRatePercent,
    includePMI: lastValidFields.includePMI,
    includeTaxesInsurance: true,
    isARM: ARM_PROGRAMS.includes(lastValidFields.loanProgram),
    dti: 0
  });
  const values = {
    fields: lastValidFields,
    calculations,
    loanProgram: lastValidFields.loanProgram
  };
  return values;
}
const safe = (fn, fallback = null) => (x) => x ? fn(x) : fallback;
function isSameFloat(float1, float2) {
  return Math.abs(Number(float1.toFixed(2)) - Number(float2.toFixed(2))) < Number.EPSILON;
}
function calculateMIPRate(ltv, loanAmount, loanprogram) {
  let monthlyMIP = 0;
  if (loanprogram == "Fixed30Year") {
    if (ltv > 90 && ltv <= 95) {
      monthlyMIP = loanAmount <= 726200 ? 5e-3 : 7e-3;
    }
    if (ltv > 95) {
      monthlyMIP = loanAmount <= 726200 ? 55e-4 : 75e-4;
    }
  } else {
    if (loanAmount >= 726200) {
      if (ltv <= 78) {
        monthlyMIP = 15e-4;
      }
      if (ltv >= 78 && ltv <= 90) {
        monthlyMIP = 4e-3;
      }
      if (ltv > 90) {
        monthlyMIP = 65e-4;
      }
    }
  }
  return monthlyMIP / 12;
}
function calculateMonthlyMIP(loanAmountValue, propertyValue, loanProgram) {
  const ltv = loanAmountValue / propertyValue * 100;
  const mipRate = calculateMIPRate(ltv, loanAmountValue, loanProgram);
  const monthlyMIP = mipRate * loanAmountValue / 12;
  return monthlyMIP;
}
function calculateMonthlyPMI(homePrice, downPaymentValue, loanProgram) {
  const loanAmount = homePrice - downPaymentValue;
  const isARM = ARM_PROGRAMS.includes(loanProgram);
  const pmi = monthlyPMI(loanAmount, homePrice, isARM);
  return pmi;
}
const vaCalculatorDisclaimerText = "The VA mortgage calculator is intended to be used for educational purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. The estimates are based on information you provide, and may not include other fees and costs that a lender may assess in addition to monthly principal and interest, such as VA funding fee, other taxes and insurance,and the actual payment obligation may be greater. Zillow Group Marketplace, Inc. does not make loans and this is not a commitment to lend.";
const mortgageCalculatorDisclaimerText = "The mortgage calculator is intended to be used for educational purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. The estimates are based on information you provide, and may not include other fees and costs that a lender may assess in addition to monthly principal and interest, such as taxes and insurance and the actual payment obligation may be greater. Zillow Group Marketplace, Inc. does not make loans and this is not a commitment to lend.";
const fhaCalculatorDisclaimerText = "The FHA mortgage calculator is intended to be used for educational purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. The estimates are based on information you provide, and may not include other fees and costs that a lender may assess in addition to monthly principal and interest, such as taxes and insurance and the actual payment obligation may be greater. Zillow Group Marketplace, Inc. does not make loans and this is not a commitment to lend.";
export {
  FieldConfig,
  calculateMonthlyMIP,
  calculateMonthlyPMI,
  fhaCalculatorDisclaimerText,
  getPayementValues,
  isSameFloat,
  mortgageCalculatorDisclaimerText,
  safe,
  useLastValidPayemntValues,
  vaCalculatorDisclaimerText
};
